import { useMemo } from "react";
import { Link } from "@tanstack/react-router";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { statusCounts } from "@/lib/looseend/selectors";
import { engineTally, PEE_ARIA } from "@/lib/looseend/pee";
import { store, useAppState } from "@/lib/looseend/store";
import { useHostedLoops } from "@/lib/loops/product/hostedLoops";

/**
 * "Your PEE™ Summary" panel over the hosted Loop list. Dismissible for the
 * rest of the day (a per-browser presentation flag, not domain state).
 */
export function PeeSummaryPanel() {
  const state = useAppState();
  const today = new Date().toISOString().slice(0, 10);
  const hosted = useHostedLoops();
  const counts = useMemo(() => statusCounts([...hosted.loops]), [hosted.loops]);
  const tally = engineTally(counts);

  if (state.peeSummaryDismissedOn === today) return null;
  if (!tally.total) return null;

  const name = state.user?.name?.split(" ")[0] || "there";
  const hour = new Date().getHours();
  const greeting = hour < 12 ? "Good morning" : hour < 18 ? "Good afternoon" : "Good evening";

  return (
    <section
      aria-label={`Your ${PEE_ARIA} summary`}
      className="content-column rounded-2xl border border-border bg-card p-4 text-left sm:p-5"
    >
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <h2 className="text-[13px] font-semibold uppercase tracking-wide text-muted-foreground">
            Your <span aria-label={PEE_ARIA}>PEE™</span> Summary
          </h2>
          <p className="mt-1.5 text-sm text-foreground">
            {greeting}, {name}. Nothing urgent changed overnight.
          </p>
          <ul className="mt-2 space-y-1 text-sm text-muted-foreground">
            {tally.actionNeeded ? (
              <li>
                {tally.actionNeeded} Loop{tally.actionNeeded === 1 ? "" : "s"} need
                {tally.actionNeeded === 1 ? "s" : ""} action
              </li>
            ) : null}
            {tally.waiting ? <li>{tally.waiting} waiting on other people</li> : null}
            {tally.readyToVerify ? <li>{tally.readyToVerify} may be ready to close</li> : null}
          </ul>
          <div className="mt-3 flex flex-wrap gap-2">
            <Button size="sm" asChild>
              <Link to="/app/queue">Review Resolution Queue</Link>
            </Button>
            <Button size="sm" variant="ghost" onClick={() => store.dismissPeeSummary()}>
              Dismiss
            </Button>
          </div>
        </div>
        <Button
          variant="ghost"
          size="icon"
          aria-label="Dismiss today's summary"
          className="-mr-1 -mt-1 size-7 shrink-0"
          onClick={() => store.dismissPeeSummary()}
        >
          <X className="size-4" aria-hidden="true" />
        </Button>
      </div>
    </section>
  );
}
